import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { AppWindow, CalendarClock, PiggyBank, Plus, Users, Wallet } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { MetricCard } from '@/components/financials/MetricCard';
import {
  AnnualOptionBadge,
  NeedsPricingBadge,
  RenewalBadge,
  SoftwareStatusBadge,
} from '@/components/catalog/SoftwareBadges';
import { SoftwareDialog } from '@/components/catalog/SoftwareDialog';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { DataTable, arrIncludesSome, type FacetedFilter } from '@/components/ui/DataTable';
import { EmptyState } from '@/components/ui/EmptyState';
import { useAuth } from '@/app/AuthProvider';
import { useSoftwareCatalog } from '@/features/software/hooks';
import {
  ANNUAL_OPTION_LABELS,
  BILLING_CYCLE_LABELS,
  BILLING_CYCLE_UNIT,
  RENEWAL_SOON_DAYS,
  SOFTWARE_CATEGORIES,
  SOFTWARE_CATEGORY_LABELS,
  SOFTWARE_STATUSES,
  SOFTWARE_STATUS_LABELS,
  isBilled,
  renewalState,
  type AnnualOptionState,
  type SoftwareRow,
} from '@/features/software/model';
import { formatCurrency, formatDateOnly } from '@/lib/formatters';
import { canWrite } from '@/lib/permissions';

const ANNUAL_FILTER_STATES: AnnualOptionState[] = [
  'cheaper',
  'available',
  'not_cheaper',
  'unknown',
  'unavailable',
  'already_annual',
  'one_time',
];

/**
 * Every app and subscription the company pays for. One row per account;
 * clicking a row opens its page with seats, logins, and billing detail.
 */
export function SoftwarePage() {
  const { role } = useAuth();
  const canEdit = canWrite(role, 'catalog');
  const navigate = useNavigate();
  const { rows, assignable, isLoading, error } = useSoftwareCatalog();
  const [creating, setCreating] = useState(false);

  const summary = useMemo(() => {
    const billed = rows.filter((r) => isBilled(r.status));
    const monthly = billed.reduce((sum, r) => sum + (r.costs.monthlyCents ?? 0), 0);
    const unpriced = billed.filter((r) => r.price_cents == null).length;
    const seatCount = rows
      .filter((r) => r.status !== 'cancelled')
      .reduce((sum, r) => sum + r.seats.length, 0);
    const people = new Set(
      rows
        .filter((r) => r.status !== 'cancelled')
        .flatMap((r) => r.seats.map((s) => s.profile_id ?? `name:${s.assignee_name ?? s.id}`)),
    ).size;
    const switchable = billed.filter((r) => r.annualState === 'cheaper');
    const savings = switchable.reduce((sum, r) => sum + (r.costs.annualSavingsCents ?? 0), 0);
    const unchecked = billed.filter((r) => r.annualState === 'unknown').length;
    const renewing = rows
      .filter((r) => renewalState(r) === 'soon')
      .sort((a, b) => (a.renews_on ?? '').localeCompare(b.renews_on ?? ''));
    return {
      monthly,
      billedCount: billed.length,
      unpriced,
      seatCount,
      people,
      savings,
      switchable: switchable.length,
      unchecked,
      renewing,
    };
  }, [rows]);

  const columns = useMemo<ColumnDef<SoftwareRow>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Software',
        size: 230,
        minSize: 160,
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate font-medium text-foreground">{row.original.name}</div>
            {(row.original.vendor || row.original.plan_name) && (
              <div className="truncate text-xs text-muted-foreground">
                {[row.original.vendor, row.original.plan_name].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
        ),
      },
      {
        id: 'category',
        accessorFn: (row) => row.category,
        header: 'Category',
        size: 170,
        filterFn: arrIncludesSome,
        cell: ({ row }) => (
          <Badge variant="secondary" className="whitespace-nowrap">
            {SOFTWARE_CATEGORY_LABELS[row.original.category]}
          </Badge>
        ),
      },
      {
        id: 'status',
        accessorFn: (row) => row.status,
        header: 'Status',
        size: 110,
        filterFn: arrIncludesSome,
        cell: ({ row }) => <SoftwareStatusBadge status={row.original.status} />,
      },
      {
        id: 'seats',
        accessorFn: (row) => row.seatNames.join(', '),
        header: 'Seats',
        size: 220,
        minSize: 140,
        sortingFn: (a, b) => a.original.seats.length - b.original.seats.length,
        cell: ({ row }) => <SeatsCell row={row.original} />,
      },
      {
        id: 'monthly',
        accessorFn: (row) => row.costs.monthlyCents ?? -1,
        header: 'Monthly',
        size: 130,
        cell: ({ row }) => <MonthlyCell row={row.original} />,
      },
      {
        id: 'billing',
        accessorFn: (row) => BILLING_CYCLE_LABELS[row.billing_cycle],
        header: 'Billed',
        size: 150,
        cell: ({ row }) =>
          row.original.price_cents == null ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span className="whitespace-nowrap tabular-nums">
              {formatCurrency(row.original.price_cents)}
              <span className="text-muted-foreground">
                {row.original.price_per_seat ? ' / seat' : ''} /{' '}
                {BILLING_CYCLE_UNIT[row.original.billing_cycle]}
              </span>
            </span>
          ),
      },
      {
        id: 'annual',
        accessorFn: (row) => row.annualState,
        header: 'Annual plan',
        size: 160,
        filterFn: arrIncludesSome,
        sortingFn: (a, b) =>
          (b.original.costs.annualSavingsCents ?? Number.MIN_SAFE_INTEGER) -
          (a.original.costs.annualSavingsCents ?? Number.MIN_SAFE_INTEGER),
        cell: ({ row }) => (
          <AnnualOptionBadge
            state={row.original.annualState}
            savingsCents={row.original.costs.annualSavingsCents}
          />
        ),
      },
      {
        id: 'renews_on',
        accessorFn: (row) => row.renews_on ?? '',
        header: 'Renews',
        size: 170,
        cell: ({ row }) =>
          row.original.renews_on ? (
            <span className="flex items-center gap-2 whitespace-nowrap">
              {formatDateOnly(row.original.renews_on)}
              <RenewalBadge renewsOn={row.original.renews_on} status={row.original.status} />
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: 'account_login',
        accessorFn: (row) => row.account_login ?? '',
        header: 'Account login',
        size: 200,
        cell: ({ row }) =>
          row.original.account_login ? (
            <span className="truncate">{row.original.account_login}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: 'started_on',
        accessorFn: (row) => row.started_on ?? '',
        header: 'Started',
        size: 120,
        cell: ({ row }) =>
          row.original.started_on ? (
            <span className="whitespace-nowrap">{formatDateOnly(row.original.started_on)}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
    ],
    [],
  );

  const filters: FacetedFilter[] = useMemo(
    () => [
      {
        columnId: 'category',
        title: 'Category',
        options: SOFTWARE_CATEGORIES.map((value) => ({
          value,
          label: SOFTWARE_CATEGORY_LABELS[value],
        })),
      },
      {
        columnId: 'status',
        title: 'Status',
        options: SOFTWARE_STATUSES.map((value) => ({
          value,
          label: SOFTWARE_STATUS_LABELS[value],
        })),
      },
      {
        columnId: 'annual',
        title: 'Annual plan',
        options: ANNUAL_FILTER_STATES.map((value) => ({
          value,
          label: ANNUAL_OPTION_LABELS[value],
        })),
      },
    ],
    [],
  );

  const nextRenewal = summary.renewing[0];

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Catalog · Software"
        title="Subscriptions"
        actions={
          canEdit ? (
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              Add software
            </Button>
          ) : undefined
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Monthly spend"
          value={formatCurrency(summary.monthly)}
          loading={isLoading}
          icon={<Wallet className="h-4 w-4" />}
          hint={`${formatCurrency(summary.monthly * 12, { whole: true })} a year across ${summary.billedCount} active${
            summary.unpriced ? ` · ${summary.unpriced} without pricing` : ''
          }`}
        />
        <MetricCard
          label="Seats assigned"
          value={String(summary.seatCount)}
          loading={isLoading}
          icon={<Users className="h-4 w-4" />}
          tone="neutral"
          hint={`${summary.people} ${summary.people === 1 ? 'person' : 'people'} across ${rows.filter((r) => r.seats.length > 0 && r.status !== 'cancelled').length} subscriptions`}
        />
        <MetricCard
          label="Annual billing could save"
          value={formatCurrency(summary.savings, { whole: true })}
          loading={isLoading}
          icon={<PiggyBank className="h-4 w-4" />}
          tone={summary.savings > 0 ? 'positive' : 'neutral'}
          hint={
            summary.switchable > 0
              ? `A year, by moving ${summary.switchable} to annual billing`
              : summary.unchecked > 0
                ? `${summary.unchecked} not checked for an annual plan yet`
                : 'Nothing to switch right now'
          }
        />
        <MetricCard
          label={`Renewing in ${RENEWAL_SOON_DAYS} days`}
          value={String(summary.renewing.length)}
          loading={isLoading}
          icon={<CalendarClock className="h-4 w-4" />}
          tone={summary.renewing.length > 0 ? 'negative' : 'neutral'}
          hint={
            nextRenewal?.renews_on
              ? `Next: ${nextRenewal.name} on ${formatDateOnly(nextRenewal.renews_on)}`
              : 'Nothing coming up'
          }
        />
      </div>

      <Card>
        <CardContent className="pt-6">
          {error ? (
            <EmptyState
              icon={AppWindow}
              title="Could not load software"
              detail={
                error instanceof Error
                  ? error.message
                  : 'Apply the latest database migration and try again.'
              }
            />
          ) : (
            <DataTable
              data={rows}
              columns={columns}
              isLoading={isLoading}
              getRowId={(row) => row.id}
              onRowClick={(row) => navigate(`/catalog/software/subscriptions/${row.id}`)}
              searchPlaceholder="Search software, vendors, people, or logins…"
              facetedFilters={filters}
              tableId="catalog-software"
              initialColumnVisibility={{ started_on: false }}
              initialPageSize={50}
              rowClassName={(row) => (row.status === 'cancelled' ? 'opacity-60' : undefined)}
              mobileCard={{
                title: (row) => row.name,
                subtitle: (row) =>
                  [row.vendor, row.plan_name].filter(Boolean).join(' · ') || undefined,
                badges: (row) => [
                  <SoftwareStatusBadge key="status" status={row.status} />,
                  row.price_cents == null ? (
                    <NeedsPricingBadge key="pricing" />
                  ) : (
                    <AnnualOptionBadge
                      key="annual"
                      state={row.annualState}
                      savingsCents={row.costs.annualSavingsCents}
                    />
                  ),
                  <RenewalBadge key="renewal" renewsOn={row.renews_on} status={row.status} />,
                ],
                amount: (row) =>
                  row.costs.monthlyCents != null
                    ? {
                        value: formatCurrency(row.costs.monthlyCents),
                        caption: 'per month',
                        tone: 'neutral',
                      }
                    : row.billing_cycle === 'one_time' && row.costs.perBillCents != null
                      ? {
                          value: formatCurrency(row.costs.perBillCents),
                          caption: 'one-time',
                          tone: 'neutral',
                        }
                      : null,
                meta: (row) => [
                  {
                    label: 'Seats',
                    value: row.seats.length
                      ? `${row.seats.length}${row.seat_count ? ` of ${row.seat_count}` : ''}`
                      : 'None',
                  },
                  ...(row.renews_on
                    ? [{ label: 'Renews', value: formatDateOnly(row.renews_on) }]
                    : []),
                  ...(row.account_login ? [{ label: 'Login', value: row.account_login }] : []),
                ],
                dimmed: (row) => row.status === 'cancelled',
              }}
              emptyState={
                <EmptyState
                  icon={AppWindow}
                  title="No software tracked yet"
                  action={
                    canEdit ? (
                      <Button onClick={() => setCreating(true)}>
                        <Plus className="h-4 w-4" />
                        Add software
                      </Button>
                    ) : undefined
                  }
                />
              }
            />
          )}
        </CardContent>
      </Card>

      <SoftwareDialog
        open={creating}
        subscription={null}
        assignedSeats={0}
        profiles={assignable}
        canEdit={canEdit}
        onOpenChange={setCreating}
        onSaved={(id) => navigate(`/catalog/software/subscriptions/${id}`)}
      />
    </div>
  );
}

function SeatsCell({ row }: { row: SoftwareRow }) {
  if (row.seats.length === 0) {
    return (
      <span className="text-muted-foreground">
        {row.seat_count ? `0 of ${row.seat_count}` : '—'}
      </span>
    );
  }
  const shown = row.seatNames.slice(0, 2).join(', ');
  const more = row.seatNames.length - 2;
  return (
    <div className="min-w-0">
      <div className="text-sm tabular-nums text-foreground">
        {row.seats.length}
        {row.seat_count ? ` of ${row.seat_count}` : ''} {row.seats.length === 1 ? 'seat' : 'seats'}
      </div>
      <div className="truncate text-xs text-muted-foreground" title={row.seatNames.join(', ')}>
        {shown}
        {more > 0 ? ` +${more}` : ''}
      </div>
    </div>
  );
}

function MonthlyCell({ row }: { row: SoftwareRow }) {
  if (row.price_cents == null) return <NeedsPricingBadge />;
  if (row.billing_cycle === 'one_time') {
    return (
      <span className="whitespace-nowrap text-muted-foreground">
        {formatCurrency(row.costs.perBillCents)} once
      </span>
    );
  }
  return (
    <span className="whitespace-nowrap font-medium tabular-nums text-foreground">
      {formatCurrency(row.costs.monthlyCents)}
    </span>
  );
}
